import React, { useState } from 'react';
import {
  Box, Container, Typography, Button, Paper, Stack, Avatar, Card, CardContent, Accordion, AccordionSummary, AccordionDetails, TextField, Alert, CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import PolicyIcon from '@mui/icons-material/Policy';
import ReportIcon from '@mui/icons-material/DescriptionOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterAltIcon from '@mui/icons-material/FilterAltOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StarIcon from '@mui/icons-material/Star';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { submitForm } from '../../utils/formSubmission';

const brandBlue = '#0B60B1';

const keyFeatures = [
  {
    icon: <PolicyIcon sx={{ fontSize: 40, color: brandBlue }} />, title: 'Reputation Monitoring',
    description: 'Track your domain and IP reputation across major blacklists and ISPs in real time.'
  },
  {
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 40, color: brandBlue }} />, title: 'Spam Filter Diagnostics',
    description: 'Analyze why emails go to spam and get actionable recommendations to fix issues.'
  },
  {
    icon: <ReportIcon sx={{ fontSize: 40, color: brandBlue }} />, title: 'Comprehensive Reporting',
    description: 'Get detailed reports on deliverability, blocklists, and sender reputation.'
  },
  {
    icon: <EmailIcon sx={{ fontSize: 40, color: brandBlue }} />, title: 'Inbox Placement Testing',
    description: 'Test and optimize your campaigns to maximize inbox delivery.'
  },
  {
    icon: <SupportAgentIcon sx={{ fontSize: 40, color: brandBlue }} />, title: 'Expert Guidance',
    description: 'Work with deliverability experts to resolve reputation issues fast.'
  }
];

const howItWorksSteps = [
  { step: 1, title: 'Connect Your Domain/IP', desc: 'Easy onboarding with guided setup.' },
  { step: 2, title: 'Monitor Reputation', desc: 'Get real-time alerts for blacklists and spam issues.' },
  { step: 3, title: 'Analyze & Act', desc: 'Receive diagnostics and expert recommendations.' },
  { step: 4, title: 'Stay Out of Spam', desc: 'Maintain a strong sender reputation and inbox placement.' }
];

const testimonials = [
  { name: 'Emily Chen', role: 'Deliverability Lead', company: 'InboxPro', quote: 'Spam0 helped us get off blacklists and keep our emails in the inbox. Essential for any sender!', rating: 5 },
  { name: 'Raj Patel', role: 'CTO', company: 'FinGuard', quote: 'The real-time alerts and diagnostics are a game changer for our team.', rating: 5 },
  { name: 'Sarah Lee', role: 'Marketing Director', company: 'BrandBoost', quote: 'We improved our open rates and reputation with Spam0\'s actionable insights.', rating: 5 },
  { name: 'David Kim', role: 'IT Manager', company: 'SecureMail', quote: 'Spam0\'s support team resolved our spam issues quickly and professionally.', rating: 5 },
  { name: 'Anna Müller', role: 'Compliance Officer', company: 'RegulaTech', quote: 'The reporting and blacklist monitoring are the best in the industry.', rating: 5 },
  { name: 'Lucas Rossi', role: 'SysAdmin', company: 'CloudOps', quote: 'Spam0 is a must-have for anyone serious about deliverability.', rating: 5 },
  { name: 'Olivia Brown', role: 'CEO', company: 'Startly', quote: 'Our sender reputation has never been better. Highly recommended!', rating: 5 },
  { name: 'Mohammed Al-Farsi', role: 'IT Security', company: 'MediTrust', quote: 'Spam0\'s analytics and alerts keep us ahead of issues.', rating: 5 },
  { name: 'Pierre Dubois', role: 'IT Consultant', company: 'Dubois IT', quote: 'The best investment for our email program.', rating: 5 },
  { name: 'Jessica Smith', role: 'Operations Director', company: 'MarketPro', quote: 'Spam0 gives us peace of mind and keeps us out of spam.', rating: 5 },
];

const faqs = [
  { q: 'How does Spam0 monitor my reputation?', a: 'Spam0 continuously checks your domain and IP against major blacklists and spam filters, alerting you to any issues.' },
  { q: 'Can Spam0 help me get delisted?', a: 'Yes! Our platform provides step-by-step guidance and expert support to help you get removed from blacklists.' },
  { q: 'What is inbox placement testing?', a: 'Inbox placement testing shows you where your emails land (inbox, spam, or promotions) across major providers.' },
  { q: 'Is my data secure?', a: 'Absolutely. Spam0 uses enterprise-grade security and encryption to protect your data.' },
];

export default function Spam0ProductPage() {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const cardsPerView = isSm ? 1 : isMd ? 2 : 3;
  const [carouselIndex, setCarouselIndex] = useState(0);
  const maxIndex = testimonials.length - cardsPerView;
  const handlePrev = () => setCarouselIndex(i => Math.max(0, i - 1));
  const handleNext = () => setCarouselIndex(i => Math.min(maxIndex, i + 1));

  // Form state
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitted(false);

    // Prepare form data with additional fields
    const formData = {
      name: form.name,
      email: form.email,
      company: form.company,
      message: form.message,
      page_name: 'spam0',
      request_for: 'Request a Demo',
      submitted_at: new Date().toISOString()
    };

    // Submit using the utility
    await submitForm(formData, {
      formName: 'Spam0 Demo Request',
      formSource: 'spam0_product_page',
      onLoading: setSubmitting,
      onSuccess: (response) => {
        setSubmitted(true);
        setForm({ name: '', email: '', company: '', message: '' });
        console.log('Demo request submitted successfully:', response);
      },
      onError: (error) => {
        setError(error.message || 'Failed to submit demo request. Please try again.');
        console.error('Demo request submission error:', error);
      }
    });
  };

  return (
    <Box sx={{ bgcolor: '#f7fafd', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box sx={{ bgcolor: brandBlue, color: '#fff', py: { xs: 6, md: 10 }, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h3" fontWeight={800} gutterBottom>Spam0</Typography>
          <Typography variant="h5" fontWeight={500} gutterBottom>
            Monitor, manage, and protect your domain & IP reputation.<br />Stay out of spam. Stay in control.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 3 }}>
            <Button
              variant="contained" color="secondary" size="large" sx={{ fontWeight: 600 }}
              href="/tools/domain-audit"
              component="a"
            >
              Audit Domain/IP
            </Button>
            <Button
              variant="outlined" color="inherit" size="large" sx={{ fontWeight: 600, borderColor: '#fff', color: '#fff' }}
              href="#request-demo"
              component="a"
            >
              Request a Demo
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Product Overview */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" fontWeight={800} color="primary" gutterBottom>
            What is Spam0?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2, textAlign: 'justify' }}>
            Spam0 is the enterprise-grade SaaS platform for proactive domain and IP reputation management. We help you monitor blacklists, diagnose spam issues, and maintain a strong sender reputation—so your emails reach the inbox, not the spam folder.
          </Typography>
        </Box>
        <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: '#e3f0fc', display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <InfoOutlinedIcon color="primary" sx={{ fontSize: 36, mt: 0.5 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="primary" gutterBottom>
              Why Reputation Matters
            </Typography>
            <Typography variant="body1" color="text.secondary">
              <b>Domain and IP reputation</b> are critical for email deliverability. If your reputation suffers, your emails may be blocked or sent to spam—even if your content is perfect. Spam0 gives you the tools and insights to stay ahead of issues and protect your brand.
            </Typography>
          </Box>
        </Paper>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'justify' }}>
          With Spam0, you get real-time monitoring, blacklist alerts, and expert guidance to keep your domain and IP reputation strong—no matter your organization's size or complexity.
        </Typography>
      </Container>

      {/* How Reputation Monitoring Works Section */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>How Reputation Monitoring Works</Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" mb={4}>
          Spam0 continuously checks your domain and IP against major blacklists, spam filters, and ISPs. Here's how the process works:
        </Typography>
        <Box sx={{ bgcolor: '#f7fafd', borderRadius: 3, p: { xs: 2, md: 4 }, mb: 3, boxShadow: 1 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="center" justifyContent="center">
            {/* Left: Domain/IP Check */}
            <Stack spacing={2} alignItems="center" sx={{ minWidth: 180 }}>
              <PolicyIcon sx={{ fontSize: 40, color: brandBlue }} />
              <Typography fontWeight={600} textAlign="center">Domain/IP Checked<br />by Spam0</Typography>
              <ArrowDownwardIcon sx={{ color: 'grey.500' }} />
              <Typography variant="body2" color="text.secondary" textAlign="center">Is your domain/IP on a blacklist?</Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <CheckCircleOutlineIcon sx={{ color: 'success.main' }} />
                <CancelIcon sx={{ color: 'error.main' }} />
              </Stack>
              <ArrowDownwardIcon sx={{ color: 'grey.500' }} />
              <Typography fontWeight={600} textAlign="center">Does your email land in the inbox?</Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 32 }} />
                <CancelIcon sx={{ color: 'error.main', fontSize: 32 }} />
              </Stack>
            </Stack>
            {/* Center: Arrows and Decision */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'center', height: 260, justifyContent: 'center' }}>
              <ArrowForwardIcon sx={{ color: 'success.main', fontSize: 40, mb: 2 }} />
              <ArrowForwardIcon sx={{ color: 'error.main', fontSize: 40, mt: 2 }} />
            </Box>
            {/* Right: Spam0 Control & Visibility */}
            <Stack spacing={2} alignItems="center" sx={{ minWidth: 220, bgcolor: '#e3f0fc', borderRadius: 2, p: 2 }}>
              <Typography fontWeight={700} color="primary" textAlign="center">Spam0 Control & Visibility</Typography>
              <ReportIcon sx={{ color: brandBlue, fontSize: 36 }} />
              <Typography variant="body2" color="text.secondary" textAlign="center">If issues are found, get instant alerts and expert recommendations:</Typography>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                <Box display="flex" flexDirection="column" alignItems="center">
                  <DeleteOutlineIcon sx={{ color: 'warning.main', fontSize: 32 }} />
                  <Typography variant="caption">Blacklist</Typography>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <CancelIcon sx={{ color: 'error.main', fontSize: 32 }} />
                  <Typography variant="caption">Spam</Typography>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <FilterAltIcon sx={{ color: 'grey.600', fontSize: 32 }} />
                  <Typography variant="caption">Clean</Typography>
                </Box>
              </Stack>
              <ArrowDownwardIcon sx={{ color: 'grey.500' }} />
              <ReportIcon sx={{ color: brandBlue, fontSize: 32 }} />
              <Typography variant="body2" color="text.secondary" textAlign="center">Get detailed reports and maintain a strong sender reputation</Typography>
            </Stack>
          </Stack>
        </Box>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Spam0 helps you stay off blacklists, out of spam, and in the inbox—where your messages belong.
        </Typography>
      </Container>

      {/* Key Features */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>Key Features</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="stretch" justifyContent="center">
          {keyFeatures.slice(0, 3).map((feature, idx) => (
            <Paper key={idx} elevation={3} sx={{ flex: 1, p: 4, textAlign: 'center', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 10 } }}>
              {feature.icon}
              <Typography variant="h6" fontWeight={600} sx={{ mt: 2 }}>{feature.title}</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>{feature.description}</Typography>
            </Paper>
          ))}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="stretch" justifyContent="center" sx={{ mt: 3 }}>
          {keyFeatures.slice(3).map((feature, idx) => (
            <Paper key={idx} elevation={3} sx={{ flex: 1, p: 4, textAlign: 'center', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 10 } }}>
              {feature.icon}
              <Typography variant="h6" fontWeight={600} sx={{ mt: 2 }}>{feature.title}</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>{feature.description}</Typography>
            </Paper>
          ))}
        </Stack>
      </Container>

      {/* How It Works */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>How It Works</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="stretch" justifyContent="center">
          {howItWorksSteps.map((step, idx) => (
            <Paper key={idx} elevation={2} sx={{ flex: 1, p: 3, textAlign: 'center', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 8 } }}>
              <Avatar sx={{ bgcolor: brandBlue, width: 48, height: 48, fontWeight: 700, mb: 1 }}>{step.step}</Avatar>
              <Typography fontWeight={600} mt={2}>{step.title}</Typography>
              <Typography variant="body2" color="text.secondary">{step.desc}</Typography>
            </Paper>
          ))}
        </Stack>
      </Container>

      {/* Testimonials Carousel */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>What Our Customers Say</Typography>
        <Box sx={{ position: 'relative', width: '100%', mt: 3, px: { xs: 0, sm: 2 } }}>
          <Button
            onClick={handlePrev}
            disabled={carouselIndex === 0}
            sx={{ position: 'absolute', left: { xs: 0, sm: -32 }, top: '50%', transform: 'translateY(-50%)', zIndex: 1, minWidth: 0, p: 1, bgcolor: '#fff', boxShadow: 2, borderRadius: '50%' }}
            aria-label="Previous"
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </Button>
          <Stack direction="row" spacing={3} justifyContent="center" alignItems="stretch" sx={{ overflow: 'hidden', width: '100%' }}>
            {testimonials.slice(carouselIndex, carouselIndex + cardsPerView).map((t, idx) => (
              <Card key={carouselIndex + idx} elevation={2} sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 8 } }}>
                <CardContent sx={{ flexGrow: 1, width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Avatar>{t.name[0]}</Avatar>
                    <Box>
                      <Typography fontWeight={600}>{t.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{t.role}, {t.company}</Typography>
                    </Box>
                  </Box>
                  <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 1, textAlign: 'justify' }}>
                    "{t.quote}"
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {[...Array(t.rating)].map((_, i) => (
                      <StarIcon key={i} sx={{ color: '#FFD700', fontSize: 20 }} />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
          <Button
            onClick={handleNext}
            disabled={carouselIndex >= maxIndex}
            sx={{ position: 'absolute', right: { xs: 0, sm: -32 }, top: '50%', transform: 'translateY(-50%)', zIndex: 1, minWidth: 0, p: 1, bgcolor: '#fff', boxShadow: 2, borderRadius: '50%' }}
            aria-label="Next"
          >
            <ArrowForwardIosIcon fontSize="small" />
          </Button>
        </Box>
      </Container>

      {/* Email Security Points + Form CTA */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={5} alignItems="stretch" justifyContent="center">
          {/* Left: Reputation Points */}
          <Box flex={1} display="flex" flexDirection="column" justifyContent="center" sx={{ mb: { xs: 4, md: 0 } }}>
            <Typography variant="h4" fontWeight={700} gutterBottom color="primary">Why Reputation Matters</Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2, color: 'text.secondary', fontSize: 18 }}>
              <li>Stay off blacklists and out of spam folders</li>
              <li>Protect your brand and customer trust</li>
              <li>Maximize deliverability and open rates</li>
              <li>Get instant alerts for reputation issues</li>
              <li>Expert support for rapid resolution</li>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Spam0 is trusted by leading organizations to keep their email reputation strong and their messages delivered.
            </Typography>
          </Box>
          {/* Right: Form */}
          <Box flex={1}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }} id="request-demo">
              <Typography variant="h5" fontWeight={600} gutterBottom>Request a Demo</Typography>
              <Box component="form" onSubmit={handleFormSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField 
                  label="Name" 
                  name="name"
                  value={form.name}
                  onChange={handleFormChange}
                  variant="outlined" 
                  required 
                  fullWidth 
                />
                <TextField 
                  label="Email" 
                  name="email"
                  value={form.email}
                  onChange={handleFormChange}
                  variant="outlined" 
                  required 
                  fullWidth 
                  type="email" 
                />
                <TextField 
                  label="Company" 
                  name="company"
                  value={form.company}
                  onChange={handleFormChange}
                  variant="outlined" 
                  required 
                  fullWidth 
                />
                <TextField 
                  label="Message or Request" 
                  name="message"
                  value={form.message}
                  onChange={handleFormChange}
                  variant="outlined" 
                  multiline 
                  minRows={3} 
                  fullWidth 
                />
                {error && <Alert severity="error">{error}</Alert>}
                {submitted && <Alert severity="success">Thank you! We've received your demo request and will be in touch soon.</Alert>}
                <Button 
                  variant="contained" 
                  color="primary" 
                  size="large" 
                  type="submit"
                  disabled={submitting}
                  sx={{ fontWeight: 600, mt: 1 }}
                  endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {submitting ? 'Sending...' : 'Request Demo'}
                </Button>
              </Box>
            </Paper>
          </Box>
        </Stack>
      </Container>

      {/* FAQ + Final CTA */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>Frequently Asked Questions</Typography>
        <Box sx={{ mb: 4 }}>
          {faqs.map((faq, idx) => (
            <Accordion key={idx} TransitionProps={{ timeout: 500 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button 
            variant="contained" 
            color="primary" 
            size="large" 
            sx={{ fontWeight: 700, px: 6, py: 2, fontSize: 20 }}
            href="#request-demo"
            component="a"
          >
            Start Free Trial
          </Button>
        </Box>
      </Container>
    </Box>
  );
} 